"use client";

import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { loggerLink, httpBatchStreamLink } from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import { toast } from "sonner";
import SuperJSON from "superjson";

import { type AppRouter } from "~/server/api/root";

declare module "@tanstack/react-query" {
  interface Register {
    queryMeta: {
      suppressGlobalErrorToast?: boolean;
    };
    mutationMeta: {
      suppressGlobalErrorToast?: boolean;
    };
  }
}

const errorsToIgnore = ["UNAUTHORIZED"];

export const createQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (
          errorsToIgnore.includes(error.message) ||
          query.meta?.suppressGlobalErrorToast
        ) {
          return;
        }

        toast.error(`Error occured: ${error.message}`);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _onMutateResult, mutation) => {
        if (
          errorsToIgnore.includes(error.message) ||
          mutation.meta?.suppressGlobalErrorToast
        ) {
          return;
        }

        toast.error(`Error occured: ${error.message}`);
      },
    }),
  });

let clientQueryClientSingleton: QueryClient | undefined = undefined;
const getQueryClient = () => {
  if (typeof window === "undefined") {
    // Server: always make a new query client
    return createQueryClient();
  }
  // Browser: use singleton pattern to keep the same query client
  return (clientQueryClientSingleton ??= createQueryClient());
};

export const api = createTRPCReact<AppRouter>();
type TRPCClient = ReturnType<typeof api.createClient>;

let clientTRPCClientSingleton: TRPCClient | undefined = undefined;

const createTRPCClient = () =>
  api.createClient({
    links: [
      loggerLink({
        enabled: (op) => shouldEnableLogger(op),
      }),
      httpBatchStreamLink({
        transformer: SuperJSON,
        url: getBaseUrl() + "/api/trpc",
        headers: () => {
          const headers = new Headers();
          headers.set("x-trpc-source", "nextjs-react");
          return headers;
        },
      }),
    ],
  });

const getTRPCClient = () => {
  if (typeof window === "undefined") {
    return createTRPCClient();
  }

  return (clientTRPCClientSingleton ??= createTRPCClient());
};

export function shouldEnableLogger(op: {
  direction: "up" | "down";
  result?: unknown;
}) {
  if (typeof window !== "undefined") {
    const { hostname } = window.location;
    const isLocalDevelopment =
      hostname === "localhost" || hostname === "127.0.0.1";

    return isLocalDevelopment || op.result instanceof Error;
  }

  return (
    process.env.NODE_ENV === "development" ||
    (op.direction === "down" && op.result instanceof Error)
  );
}

export function TRPCReactProvider(props: { children: React.ReactNode }) {
  const queryClient = getQueryClient();
  const trpcClient = getTRPCClient();

  return (
    <QueryClientProvider client={queryClient}>
      <api.Provider client={trpcClient} queryClient={queryClient}>
        {props.children}
      </api.Provider>
    </QueryClientProvider>
  );
}

export function getBaseUrl() {
  if (typeof window !== "undefined") return window.location.origin;
  if (process.env.VERCEL_URL) return `https://${process.env.VERCEL_URL}`;
  return `http://localhost:${process.env.PORT ?? 3000}`;
}
